import { ArrowLeft, ChevronLeft, ChevronRight, Instagram, Layers, Mail, MapPin, PieChart, Phone, Search, Sparkles, Store, Users, X } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProspectionList, updateLead } from "../../interfaces/prospectionLists";
import { IProspectionList, ILead, LeadStatus, ContactMethod } from "../../interfaces/prospectionList";
import ProspectionTableV2, { STATUS_TONE } from "../../components/prospection/ProspectionTableV2";
import CityAutocomplete from "../../components/fields/CityAutocomplete";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { useEdgeAlignedDropdown } from "../../hooks/useEdgeAlignedDropdown";

// ── Constants ────────────────────────────────────────────────────────────────

const CONTACT_METHODS: { key: ContactMethod; label: string; icon: React.ReactNode; tone: "neutral" | "accent" | "success" | "info" }[] = [
    { key: "phone", label: "Téléphone", icon: <Phone className="h-3.5 w-3.5" />, tone: "neutral" },
    { key: "instagram", label: "Instagram", icon: <Instagram className="h-3.5 w-3.5" />, tone: "accent" },
    { key: "email", label: "E-mail", icon: <Mail className="h-3.5 w-3.5" />, tone: "success" },
    { key: "visit", label: "Visite", icon: <MapPin className="h-3.5 w-3.5" />, tone: "info" },
];

// Tone → Tailwind classes, mirroring the Badge component's active-tab recipe (bg-{tone}-subtle etc.)
const TONE_ACTIVE_CLASSES: Record<string, string> = {
    neutral: "bg-surface-sunken text-ink border-border-strong",
    accent: "bg-accent-subtle text-accent border-accent/30",
    success: "bg-success-subtle text-success border-success/30",
    info: "bg-info-subtle text-info border-info/30",
    warning: "bg-warning-subtle text-warning border-warning/30",
    danger: "bg-danger-subtle text-danger border-danger/30",
};
const TONE_DOT_CLASSES: Record<string, string> = {
    neutral: "bg-ink-faint",
    accent: "bg-accent",
    success: "bg-success",
    warning: "bg-warning",
    info: "bg-info",
    danger: "bg-danger",
};

const STATUSES: { key: LeadStatus; label: string }[] = [
    { key: "to_contact",       label: "À contacter" },
    { key: "linkedin_pending", label: "Invit. LinkedIn" },
    { key: "nrp1",             label: "NRP 1" },
    { key: "nrp2",             label: "NRP 2" },
    { key: "nrp3",             label: "NRP 3" },
    { key: "contacted",        label: "Contacté" },
    { key: "suivi",            label: "Suivi" },
    { key: "to_followup",      label: "À relancer" },
    { key: "ignored",          label: "Ignoré" },
];

const SOURCE_CONFIG = {
    database: {
        label: "Base de données",
        icon: <Store className="h-5 w-5 text-ink-inverse" />,
    },
    signals: {
        label: "Signaux business",
        icon: <Sparkles className="h-5 w-5 text-ink-inverse" />,
    },
};

// ── Main ProspectionView ──────────────────────────────────────────────────────

export default function ProspectionView() {
    // ── State ─────────────────────────────────────────────────────────────────
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [list, setList] = useState<IProspectionList | null>(null);
    const [loading, setLoading] = useState(true);

    const isMounted = useRef(false);

    const loadSavedState = () => {
        try {
            const s = localStorage.getItem(`prospection_${id}`);
            return s ? JSON.parse(s) : null;
        } catch { return null; }
    };

    const savedState = loadSavedState();

    // ── Filter state ──────────────────────────────────────────────────────────
    const [activeMethod, setActiveMethod] = useState<ContactMethod>(savedState?.activeMethod ?? "phone");
    const [statusFilter, setStatusFilter] = useState<string>(savedState?.statusFilter ?? "all");
    const [search, setSearch] = useState<string>(savedState?.search ?? "");

    const [page, setPage] = useState<number>(savedState?.page ?? 1);
    const itemsPerPage = 20;

    const [cityFilter, setCityFilter] = useState<string>(savedState?.cityFilter ?? '');
    const [minScore, setMinScore] = useState<number>(savedState?.minScore ?? 0);
    const [contactFilters, setContactFilters] = useState<string[]>(savedState?.contactFilters ?? []);
    const [establishmentType, setEstablishmentType] = useState<string[]>(savedState?.establishmentType ?? []);
    const [concept, setConcept] = useState<string[]>(savedState?.concept ?? []);

    // ── Dropdown visibility ───────────────────────────────────────────────────
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const [showEstablishmentDropdown, setShowEstablishmentDropdown] = useState(false);

    // Edge-aware alignment: flip each dropdown to the right edge of its trigger
    // when it would otherwise overflow past the right side of the viewport.
    const contactDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showContactDropdown);
    const establishmentDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showEstablishmentDropdown);
    const [_showConceptDropdown, setShowConceptDropdown] = useState(false);
    const [_conceptSearch, _setConceptSearch] = useState('');

    const [availableCategories, setAvailableCategories] = useState<string[]>([]);
    const [_availableSubCategories, setAvailableSubCategories] = useState<string[]>([]);

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        getProspectionList(id)
            .then(data => {
                setList(data);
                const cats = new Set<string>();
                const subCats = new Set<string>();
                data.leads.forEach((lead: ILead) => {
                    const est = lead.establishment as any;
                    if (est?.categories) est.categories.forEach((c: string) => cats.add(c));
                    if (est?.subCategories) est.subCategories.forEach((sc: string) => subCats.add(sc));
                });
                setAvailableCategories(Array.from(cats).sort());
                setAvailableSubCategories(Array.from(subCats).sort());
            })
            .catch((err) => console.error("Failed to load list:", err))
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.relative')) {
                setShowContactDropdown(false);
                setShowEstablishmentDropdown(false);
                setShowConceptDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        localStorage.setItem(`prospection_${id}`, JSON.stringify({
            page, activeMethod, statusFilter, search,
            cityFilter, minScore, contactFilters, establishmentType, concept,
        }));
    }, [id, page, activeMethod, statusFilter, search, cityFilter, minScore, contactFilters, establishmentType, concept]);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        setPage(1);
    }, [search, cityFilter, minScore, contactFilters, establishmentType, concept, statusFilter]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleLeadUpdate = useCallback((establishmentId: string, patch: Partial<ILead>) => {
        setList((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                leads: prev.leads.map((l) =>
                    l.establishmentId === establishmentId ? { ...l, ...patch } : l
                ),
            };
        });
        if (id) {
            updateLead(id, establishmentId, patch).catch((err) => {
                console.error("Failed to update lead:", err);
            });
        }
    }, [id]);

    // ── Guards ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-canvas font-sans">
                <p className="text-sm text-ink-faint">Chargement…</p>
            </div>
        );
    }

    if (!list) {
        return (
            <div className="flex items-center justify-center h-screen bg-canvas font-sans">
                <div className="text-center">
                    <Store className="h-10 w-10 text-ink-faint mx-auto mb-3" />
                    <p className="text-sm font-medium text-ink mb-3">Liste introuvable</p>
                    <button
                        onClick={() => navigate("/prospection")}
                        className="px-3 py-1.5 bg-ink text-ink-inverse text-sm rounded-lg hover:bg-ink/90 transition-colors duration-150"
                    >
                        Retour aux listes
                    </button>
                </div>
            </div>
        );
    }

    // ── Derived data ──────────────────────────────────────────────────────────

    const source = (list.filters?.source || "database") as "database" | "signals";
    const sourceConfig = SOURCE_CONFIG[source];
    // The default "Mes clients" list with nothing in it yet gets an onboarding screen instead
    // of filters over an empty table — those would just be clutter above a "get started" message.
    const isEmptyDefaultList = list.leads.length === 0 && !!list.isDefault;

    const statusCounts = list.leads.reduce(
        (acc, l) => { acc[l.status] = (acc[l.status] || 0) + 1; return acc; },
        {} as Record<string, number>
    );

    // ── Filter logic ──────────────────────────────────────────────────────────

    const filteredLeads = list.leads.filter((lead) => {
        const est = lead.establishment as any;

        if (statusFilter !== "all" && lead.status !== statusFilter) return false;

        if (search) {
            const name = (est?.name || "").toLowerCase();
            const city = (est?.address?.city || "").toLowerCase();
            const email = (est?.email || est?.peoples?.[0]?.email || "").toLowerCase();
            const q = search.toLowerCase();
            if (!name.includes(q) && !city.includes(q) && !email.includes(q)) return false;
        }

        if (cityFilter && (!est?.address?.city || !est.address.city.toLowerCase().includes(cityFilter.toLowerCase()))) {
            return false;
        }

        if (minScore > 0 && (est?.score || 0) < minScore) {
            return false;
        }

        if (contactFilters.length > 0) {
            const hasRequiredContacts = contactFilters.every(filter => {
                if (filter === 'email') return est?.email || est?.peoples?.[0]?.email;
                if (filter === 'phone') return est?.phone || est?.peoples?.[0]?.phone;
                if (filter === 'linkedin') return est?.social_networks?.some((sn: any) => sn.platform === 'linkedin');
                if (filter === 'instagram') return est?.social_networks?.some((sn: any) => sn.platform === 'instagram');
                if (filter === 'website') return est?.website;
                return false;
            });
            if (!hasRequiredContacts) return false;
        }

        if (establishmentType.length > 0) {
            if (!est?.categories || !est.categories.some((c: string) => establishmentType.includes(c))) {
                return false;
            }
        }

        if (concept.length > 0) {
            if (!est?.subCategories || !est.subCategories.some((sc: string) => concept.includes(sc))) {
                return false;
            }
        }

        return true;
    });

    // ── Pagination ────────────────────────────────────────────────────────────

    const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
    const startIdx = (page - 1) * itemsPerPage;
    const paginatedLeads = filteredLeads.slice(startIdx, startIdx + itemsPerPage);

    const activeFilterCount = [
        search, cityFilter,
        minScore > 0 ? String(minScore) : '',
        ...contactFilters, ...establishmentType, ...concept,
    ].filter(Boolean).length;

    const clearAllFilters = () => {
        setSearch("");
        setCityFilter('');
        setMinScore(0);
        setContactFilters([]);
        setEstablishmentType([]);
        setConcept([]);
    };

    const establishmentTypeOptions = availableCategories.map(cat => ({ value: cat, label: cat }));

    const goToPage = (pageNumber: number) => {
        setPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;
        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            const startPage = Math.max(2, page - 1);
            const endPage = Math.min(totalPages - 1, page + 1);
            if (startPage > 2) pages.push('...');
            for (let i = startPage; i <= endPage; i++) pages.push(i);
            if (endPage < totalPages - 1) pages.push('...');
            if (totalPages > 1) pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-6">

                {/* Back */}
                <button
                    onClick={() => navigate("/prospection")}
                    className="flex items-center gap-1.5 text-ink-muted hover:text-ink mb-6 text-xs transition-colors duration-150"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Retour aux listes
                </button>

                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-ink flex items-center justify-center flex-shrink-0">
                        {sourceConfig.icon}
                    </div>
                    <div>
                        <h1 className="font-sans text-xl font-semibold text-ink">{list.name}</h1>
                        {list.description && (
                            <p className="text-sm text-ink-muted mt-0.5">{list.description}</p>
                        )}
                    </div>
                </div>

                {!isEmptyDefaultList && <>

                {/* Status breakdown */}
                <div className="rounded-2xl border border-border bg-surface p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-semibold text-ink">Répartition par statut</h2>
                        <span className="text-xs text-ink-muted flex items-center gap-1.5">
                            {list.leads.length.toLocaleString("fr-FR")}{list.isCapped ? "+" : ""} lead{list.leads.length !== 1 ? "s" : ""}
                            {list.isCapped && (
                                <span className="px-1 py-0.5 rounded-sm bg-warning-subtle text-warning text-[10px] font-semibold uppercase tracking-wide">Max</span>
                            )}
                        </span>
                    </div>
                    {/* Segmented bar */}
                    <div className="flex h-3 w-full rounded-full overflow-hidden bg-surface-sunken">
                        {STATUSES.filter((s) => (statusCounts[s.key] || 0) > 0).map((s) => {
                            const tone = STATUS_TONE[s.key];
                            const isActive = statusFilter === s.key;
                            const count = statusCounts[s.key] || 0;
                            const pct = list.leads.length > 0 ? (count / list.leads.length) * 100 : 0;
                            return (
                                <button
                                    key={s.key}
                                    onClick={() => setStatusFilter(isActive ? "all" : s.key)}
                                    title={`${s.label} — ${count} lead${count !== 1 ? "s" : ""} (${Math.round(pct)}%)`}
                                    style={{ width: `${pct}%` }}
                                    className={`h-full transition-opacity duration-150 ${TONE_DOT_CLASSES[tone]} ${
                                        statusFilter !== "all" && !isActive ? "opacity-30" : "opacity-100"
                                    }`}
                                />
                            );
                        })}
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
                        {STATUSES.map((s) => {
                            const tone = STATUS_TONE[s.key];
                            const isActive = statusFilter === s.key;
                            const count = statusCounts[s.key] || 0;
                            const pct = list.leads.length > 0 ? Math.round((count / list.leads.length) * 100) : 0;
                            return (
                                <button
                                    key={s.key}
                                    onClick={() => setStatusFilter(isActive ? "all" : s.key)}
                                    title={`${s.label} — ${count} lead${count !== 1 ? "s" : ""} (${pct}%)`}
                                    className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded-md transition-colors duration-150 ${
                                        isActive ? "bg-surface-sunken" : "hover:bg-surface-sunken/60"
                                    }`}
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${TONE_DOT_CLASSES[tone]}`} />
                                    <span className={`text-xs leading-tight ${isActive ? "text-ink font-medium" : "text-ink-muted"}`}>
                                        {s.label}
                                    </span>
                                    <span className="text-xs tabular-nums text-ink-faint">
                                        {count.toLocaleString("fr-FR")} · {pct}%
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Contact Method Tabs */}
                <div className="mb-4 flex items-center gap-2">
                    <span className="text-xs text-ink-muted">Canal :</span>
                    {CONTACT_METHODS.map((m) => {
                        const isActive = activeMethod === m.key;
                        const colorClasses = isActive
                            ? TONE_ACTIVE_CLASSES[m.tone]
                            : "bg-surface text-ink-muted border-border";

                        return (
                            <button
                                key={m.key}
                                onClick={() => setActiveMethod(m.key)}
                                className={`h-8 px-2.5 text-xs font-medium rounded-lg transition-colors duration-150 flex items-center gap-1.5 border hover:border-border-strong ${colorClasses}`}
                            >
                                {m.icon}
                                {m.label}
                            </button>
                        );
                    })}
                </div>

                {/* Filters Bar */}
                <div className="mb-4 flex items-center gap-2 flex-wrap">
                    {/* Search */}
                    <div className="relative flex items-center">
                        <Search className="w-3.5 h-3.5 text-ink-faint absolute left-2.5" />
                        <Input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher un établissement…"
                            className="h-8 pl-7 pr-2.5 text-xs w-60"
                        />
                    </div>

                    {/* City */}
                    <CityAutocomplete
                        value={cityFilter}
                        onChange={setCityFilter}
                    />

                    {/* Status filter */}
                    <Select
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={[
                            { value: "all", label: "Tous les statuts" },
                            ...STATUSES.map((s) => ({ value: s.key, label: s.label })),
                        ]}
                    />

                    {/* Coordonnées */}
                    <div className="relative">
                        <button
                            onClick={() => {
                                setShowContactDropdown(!showContactDropdown);
                                setShowEstablishmentDropdown(false);
                                setShowConceptDropdown(false);
                            }}
                            className={`h-8 px-2.5 text-xs font-medium rounded-lg transition-colors duration-150 flex items-center gap-1.5 border ${
                                contactFilters.length > 0
                                    ? 'bg-surface-sunken text-ink border-border-strong'
                                    : 'bg-surface text-ink-muted border-border hover:border-border-strong'
                            }`}
                        >
                            <Phone className="w-3.5 h-3.5" />
                            Coordonnées
                            {contactFilters.length > 0 && (
                                <span className="px-1 py-0.5 bg-ink text-ink-inverse rounded-sm text-[10px] font-bold">
                                    {contactFilters.length}
                                </span>
                            )}
                        </button>
                        {showContactDropdown && (
                            <div ref={contactDropdownAlign.ref} className={`absolute top-full mt-1 ${contactDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[160px] py-1`}>
                                {['linkedin', 'instagram', 'email', 'phone', 'website'].map(contact => (
                                    <label key={contact} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={contactFilters.includes(contact)}
                                            onChange={(e) => {
                                                setContactFilters(e.target.checked
                                                    ? [...contactFilters, contact]
                                                    : contactFilters.filter(f => f !== contact)
                                                );
                                            }}
                                            className="w-3.5 h-3.5 rounded-sm border-border"
                                        />
                                        <span className="text-xs text-ink capitalize">{contact}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Type établissement */}
                    {establishmentTypeOptions.length > 0 && (
                        <div className="relative">
                            <button
                                onClick={() => {
                                    setShowEstablishmentDropdown(!showEstablishmentDropdown);
                                    setShowContactDropdown(false);
                                    setShowConceptDropdown(false);
                                }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-lg transition-colors duration-150 flex items-center gap-1.5 border ${
                                    establishmentType.length > 0
                                        ? 'bg-warning-subtle text-warning border-warning/30'
                                        : 'bg-surface text-ink-muted border-border hover:border-border-strong'
                                }`}
                            >
                                <Sparkles className="w-3.5 h-3.5" />
                                Type
                                {establishmentType.length > 0 && (
                                    <span className="px-1 py-0.5 bg-warning text-white rounded-sm text-[10px] font-bold">
                                        {establishmentType.length}
                                    </span>
                                )}
                            </button>
                            {showEstablishmentDropdown && (
                                <div ref={establishmentDropdownAlign.ref} className={`absolute top-full mt-1 ${establishmentDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[200px] max-h-[280px] overflow-y-auto py-1`}>
                                    {establishmentTypeOptions.map(option => (
                                        <label key={option.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={establishmentType.includes(option.value)}
                                                onChange={(e) => {
                                                    setEstablishmentType(e.target.checked
                                                        ? [...establishmentType, option.value]
                                                        : establishmentType.filter(t => t !== option.value)
                                                    );
                                                }}
                                                className="w-3.5 h-3.5 rounded-sm border-border"
                                            />
                                            <span className="text-xs text-ink">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Clear */}
                    {activeFilterCount > 0 && (
                        <button
                            onClick={clearAllFilters}
                            className="h-8 px-2.5 text-xs text-ink-muted hover:text-danger hover:bg-danger-subtle border border-border hover:border-danger/30 rounded-lg transition-colors duration-150 flex items-center gap-1.5"
                        >
                            <X className="w-3.5 h-3.5" />
                            Réinitialiser
                        </button>
                    )}

                    <span className="text-xs text-ink-faint ml-auto">
                        {filteredLeads.length} résultat{filteredLeads.length !== 1 ? "s" : ""}
                    </span>
                </div>

                </>}

                {/* Leads table */}
                {isEmptyDefaultList ? (
                    <div className="rounded-2xl border border-border bg-surface overflow-hidden">
                        <div className="px-6 py-10 md:px-10 md:py-12 max-w-2xl mx-auto text-center">
                            <div className="w-14 h-14 rounded-2xl bg-ink flex items-center justify-center mx-auto mb-5">
                                <Users className="w-7 h-7 text-ink-inverse" />
                            </div>
                            <h2 className="text-lg font-semibold text-ink mb-2">Bienvenue dans votre liste "Mes clients"</h2>
                            <p className="text-sm text-ink-muted leading-relaxed mb-8">
                                C'est ici que vous listez les établissements déjà signés, vos vrais clients, pas des
                                prospects. Recherchez-les et ajoutez-les : DuckFlow s'en sert ensuite pour comprendre
                                qui ils sont.
                            </p>

                            <div className="grid sm:grid-cols-3 gap-3 mb-8 text-left">
                                <div className="rounded-xl border border-border bg-canvas p-4">
                                    <Search className="w-5 h-5 text-ink-faint mb-2" />
                                    <p className="text-xs font-semibold text-ink mb-1">1. Ajoutez vos clients</p>
                                    <p className="text-[11px] text-ink-muted leading-relaxed">
                                        Recherchez chaque établissement déjà signé et ajoutez-le à cette liste.
                                    </p>
                                </div>
                                <div className="rounded-xl border border-border bg-canvas p-4">
                                    <PieChart className="w-5 h-5 text-ink-faint mb-2" />
                                    <p className="text-xs font-semibold text-ink mb-1">2. Passez à l'analyse</p>
                                    <p className="text-[11px] text-ink-muted leading-relaxed">
                                        Intelligence clients repère les points communs entre vos restaurants et les
                                        regroupe en segments.
                                    </p>
                                </div>
                                <div className="rounded-xl border border-border bg-canvas p-4">
                                    <Layers className="w-5 h-5 text-ink-faint mb-2" />
                                    <p className="text-xs font-semibold text-ink mb-1">3. Repérez vos tendances</p>
                                    <p className="text-[11px] text-ink-muted leading-relaxed">
                                        Chaque segment affiche son poids en % de votre portefeuille, et un prompt
                                        pour retrouver des établissements similaires.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center justify-center gap-2.5">
                                <button
                                    onClick={() => navigate("/database")}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-ink text-ink-inverse text-sm font-medium rounded-lg hover:bg-ink/90 transition-colors duration-150"
                                >
                                    <Search className="w-4 h-4" />
                                    Rechercher mes clients
                                </button>
                                <button
                                    onClick={() => navigate("/client-types")}
                                    className="flex items-center gap-1.5 px-4 py-2 text-ink text-sm font-medium rounded-lg border border-border-strong hover:bg-surface-sunken transition-colors duration-150"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    Voir Intelligence clients
                                </button>
                            </div>
                        </div>
                    </div>
                ) : filteredLeads.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 bg-surface border border-border rounded-2xl">
                        <Store className="h-8 w-8 text-ink-faint mb-3" />
                        <p className="text-sm font-medium text-ink mb-1">Aucun lead trouvé</p>
                        <p className="text-xs text-ink-faint">
                            {search ? "Essayez un autre terme de recherche" : "Ajustez vos filtres"}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="rounded-2xl border border-border overflow-hidden">
                            <ProspectionTableV2
                                leads={paginatedLeads}
                                onLeadUpdate={handleLeadUpdate}
                            />
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-4 flex items-center justify-between bg-surface rounded-2xl border border-border px-4 py-2.5">
                                <span className="text-xs text-ink-muted">
                                    <span className="font-medium text-ink">{startIdx + 1}</span>
                                    {' – '}
                                    <span className="font-medium text-ink">{Math.min(startIdx + itemsPerPage, filteredLeads.length)}</span>
                                    {' sur '}
                                    <span className="font-medium text-ink">{filteredLeads.length}</span>
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => goToPage(page - 1)}
                                        disabled={page === 1}
                                        className="p-1.5 border border-border rounded-lg hover:border-border-strong disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                                    >
                                        <ChevronLeft className="w-3.5 h-3.5 text-ink-muted" />
                                    </button>
                                    <div className="flex items-center gap-0.5">
                                        {getPageNumbers().map((pageNum, index) => (
                                            pageNum === '...' ? (
                                                <span key={`ellipsis-${index}`} className="px-2 text-xs text-ink-faint">…</span>
                                            ) : (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => goToPage(pageNum as number)}
                                                    className={`min-w-[28px] h-7 px-1.5 rounded-lg text-xs font-medium transition-colors duration-150 ${
                                                        page === pageNum
                                                            ? 'bg-ink text-ink-inverse'
                                                            : 'border border-border text-ink-muted hover:border-border-strong'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            )
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => goToPage(page + 1)}
                                        disabled={page === totalPages}
                                        className="p-1.5 border border-border rounded-lg hover:border-border-strong disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                                    >
                                        <ChevronRight className="w-3.5 h-3.5 text-ink-muted" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
